// =========================================================================
// studyDescriptionNormalize.js
// =========================================================================
//
// Fuzzy bucketing for free-text DICOM study descriptions.
//
// The "study_description" field on rpt_study comes straight from the
// modality and is wildly inconsistent: every site capitalizes
// differently, abbreviates differently, may or may not append a CPT
// code, may pluralize/singularize, and may reorder words. The result is
// a long tail with hundreds of "different" descriptions that are
// actually the same procedure spelled differently.
//
// We collapse them in two passes:
//
//   Pass 1 — canonicalize: lowercase, strip numeric procedure codes,
//            strip punctuation, drop filler/qualifier words, run each
//            token through a synonym dictionary, alpha-sort the
//            resulting token bag, and join with spaces. Identical
//            canonical keys collapse into one bucket.
//
//   Pass 2 — token-Jaccard merge: walk the buckets in descending count
//            order; for each bucket, scan smaller buckets and merge
//            them into the current one if their token sets overlap by
//            >= JACCARD_THRESHOLD. Catches off-by-one filler differences
//            that the synonym dictionary missed (e.g.
//            "ct heart structure morphology" vs
//            "ct heart structure morphology function").
//
// Display label is the most-frequent original spelling, so the user
// sees a familiar string on the chart axis instead of our normalized
// bag-of-words.

// Token-level synonyms. Keys MUST be lowercase, alphanumeric only, and
// the values become the canonical token. Add aggressively — false
// merges show up as "wait, why is X grouped with Y?" and are easy to
// catch in QA, while missed merges silently bloat the long tail.
const SYNONYMS = {
  // ---- modality words -------------------------------------------------
  echo: 'echocardiogram',
  echocardiography: 'echocardiogram',
  ct: 'ct',
  cta: 'cta',
  ctv: 'ctv',
  mri: 'mri',
  mra: 'mra',
  mrv: 'mrv',
  us: 'ultrasound',
  ultrasonography: 'ultrasound',
  xr: 'xray',
  xray: 'xray',
  radiograph: 'xray',
  radiography: 'xray',
  nm: 'nuclearmedicine',
  pet: 'pet',
  // ---- anatomy abbreviations -----------------------------------------
  abd: 'abdomen',
  abdominal: 'abdomen',
  pelv: 'pelvis',
  cerv: 'cervical',
  thor: 'thoracic',
  lum: 'lumbar',
  ext: 'extremity',
  uppextr: 'upperextremity',
  lowextr: 'lowerextremity',
  // ---- procedure abbreviations ---------------------------------------
  cath: 'catheterization',
  angio: 'angiography',
  arthro: 'arthrography',
  myelo: 'myelography',
  str: 'structure',
  morph: 'morphology',
  func: 'function',
  // ---- laterality ----------------------------------------------------
  lt: 'left',
  rt: 'right',
  bilat: 'bilateral',
  bil: 'bilateral',
  // ---- contrast directives -------------------------------------------
  // (kept distinct so "with contrast" never collapses with "without contrast")
  wc: 'withcontrast',
  woc: 'withoutcontrast',
  wo: 'without',
  w: 'with',
  // ---- common spelling collapses -------------------------------------
  cardio: 'cardiac',
  ortho: 'orthopedic',
};

// Tokens dropped entirely — qualifiers / fillers that don't change
// what procedure was performed. Keep meaningful clinical words OUT of
// this list (e.g. "with"/"without" are routed through SYNONYMS instead
// so the contrast distinction is preserved).
const DROP_TOKENS = new Set([
  'a', 'an', 'the', 'of', 'and', 'or', 'in', 'on', 'to', 'for',
  'study', 'studies',
  'scan', 'scans',
  'exam', 'exams', 'examination',
  'image', 'images', 'imaging',
  'view', 'views',
  'complete',
  'limited',
  'routine',
  'standard',
  'procedure',
  'protocol',
  'unspecified',
  'misc', 'miscellaneous',
  'other',
  'series',
]);

// Two buckets get merged in Pass 2 if their token-set Jaccard is at
// least this. 0.75 lets a 4-token vs 5-token pair merge (4 in common,
// 5 in union → 0.8) but keeps a 3-token vs 5-token pair separate
// (3 in common, 5 in union → 0.6). Tweak with care; lowering causes
// false merges, raising leaves duplicates on the chart.
const JACCARD_THRESHOLD = 0.75;

/** Strip CPT/HCPCS codes (4–5 digit numbers) anywhere in the string. */
function stripProcedureCodes(s) {
  return s.replace(/\b\d{4,6}\b/g, ' ');
}

/** Tokenize: punctuation/slashes → space, then split on whitespace. */
function tokenize(s) {
  return s
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Returns the canonical key for a single raw description. Two raw
 * strings that should bucket together MUST produce the same key here.
 */
export function canonicalDescriptionKey(raw) {
  if (!raw) return '';
  let s = String(raw).toLowerCase().trim();
  if (!s) return '';
  s = stripProcedureCodes(s);

  const tokens = tokenize(s)
    .map(t => SYNONYMS[t] || t)
    .filter(t => !DROP_TOKENS.has(t));

  if (tokens.length === 0) return '';

  // Sort so word order doesn't create false splits ("Heart CT" vs
  // "CT Heart"). De-dupe so "ct ct chest" doesn't outrank "ct chest".
  const unique = Array.from(new Set(tokens)).sort();
  return unique.join(' ');
}

/** Token-set Jaccard similarity between two canonical keys. */
function jaccard(keyA, keyB) {
  if (keyA === keyB) return 1;
  const a = new Set(keyA.split(' '));
  const b = new Set(keyB.split(' '));
  if (a.size === 0 || b.size === 0) return 0;
  let intersect = 0;
  for (const t of a) if (b.has(t)) intersect++;
  const union = a.size + b.size - intersect;
  return intersect / union;
}

/**
 * Bucket raw study descriptions into "same procedure" groups.
 *
 * @param {Iterable<string>} rawDescriptions  raw description strings,
 *                                            duplicates allowed
 * @returns {Map<string, { count, label, originals: Map<string, number> }>}
 *          keyed by canonical key. `count` is the bucket total,
 *          `label` is the most common original spelling (best
 *          display label for the chart), and `originals` exposes the
 *          full spelling histogram for tooltips/debugging.
 */
export function bucketStudyDescriptions(rawDescriptions) {
  // Pass 1: canonical-key bucketing.
  const buckets = new Map();
  for (const raw of rawDescriptions) {
    if (!raw) continue;
    const original = String(raw).trim();
    if (!original) continue;
    const key = canonicalDescriptionKey(original);
    if (!key) continue;
    let b = buckets.get(key);
    if (!b) {
      b = { count: 0, originals: new Map() };
      buckets.set(key, b);
    }
    b.count++;
    b.originals.set(original, (b.originals.get(original) || 0) + 1);
  }

  // Pass 2: Jaccard merge of near-duplicate buckets. Walk in count
  // descending so smaller buckets always merge UP into the bigger one
  // (the bigger bucket's top original wins as the display label).
  const sortedKeys = Array.from(buckets.keys())
    .sort((a, b) => buckets.get(b).count - buckets.get(a).count);
  const merged = new Set();
  for (let i = 0; i < sortedKeys.length; i++) {
    const keyA = sortedKeys[i];
    if (merged.has(keyA)) continue;
    const bucketA = buckets.get(keyA);
    for (let j = i + 1; j < sortedKeys.length; j++) {
      const keyB = sortedKeys[j];
      if (merged.has(keyB)) continue;
      if (jaccard(keyA, keyB) >= JACCARD_THRESHOLD) {
        const bucketB = buckets.get(keyB);
        bucketA.count += bucketB.count;
        for (const [orig, cnt] of bucketB.originals) {
          bucketA.originals.set(orig, (bucketA.originals.get(orig) || 0) + cnt);
        }
        merged.add(keyB);
        buckets.delete(keyB);
      }
    }
  }

  // Pick a display label per bucket: the original spelling with the
  // highest count. Ties are broken by the longest (more descriptive)
  // string; falls back to alphabetical for stability.
  for (const bucket of buckets.values()) {
    let bestLabel = '';
    let bestCount = -1;
    for (const [orig, cnt] of bucket.originals) {
      if (cnt > bestCount ||
          (cnt === bestCount && orig.length > bestLabel.length) ||
          (cnt === bestCount && orig.length === bestLabel.length && orig < bestLabel)) {
        bestCount = cnt;
        bestLabel = orig;
      }
    }
    bucket.label = bestLabel;
  }

  return buckets;
}
